import * as Sentry from "@sentry/react";
import type { ApiError } from "../lib/api/types";
import { ApiErrorWithStack } from "../lib/api/types";

const defaultApiBase = import.meta.env.VITE_API_BASE_URL
  ?? (import.meta.env.PROD ? "https://prod.g.pistepal.app/v1beta" : "http://localhost/v1beta")
export const API_BASE_URL = defaultApiBase.replace(/\/$/, "")

type ApiOptions = RequestInit & { token: string }

function generateRequestId(): string {
  return Math.random().toString(36).slice(2, 8);
}

async function handleApiResponse<T>(response: Response, requestInfo: { url: string; method: string }): Promise<T> {
  if (!response.ok) {
    // If we get a 401, the Firebase token is invalid
    if (response.status === 401) {
      throw new ApiErrorWithStack(
        'Your session has expired. Please sign in again.',
        new Error('Session invalid'),
        requestInfo
      );
    }

    let errorData: ApiError;
    try {
      errorData = await response.json();
    } catch (parseError) {
      // Handle empty or malformed error responses gracefully
      throw new ApiErrorWithStack(
        `Server error (${response.status})`,
        new Error('Server returned empty or malformed error response'),
        requestInfo
      );
    }

    const error = new ApiErrorWithStack(
      errorData.Error || 'An error occurred',
      new Error(errorData.Stack || 'No stack trace available'),
      requestInfo,
      errorData.TraceId,
      errorData.Code
    );

    // Capture error in Sentry
    Sentry.captureException(error, {
      tags: {
        traceId: errorData.TraceId,
        errorCode: errorData.Code,
      },
      extra: {
        requestInfo,
        errorData,
      },
    });

    throw error;
  }

  // Handle empty response bodies gracefully
  const contentType = response.headers.get('content-type');
  const contentLength = response.headers.get('content-length');

  // If there's no content or it's not JSON, return empty object
  if (contentLength === '0' || !contentType?.includes('application/json')) {
    return {} as T;
  }

  const text = await response.text();
  if (!text.trim()) {
    return {} as T;
  }

  try {
    return JSON.parse(text) as T;
  } catch (error) {
    throw new Error(`JSON Parse error: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

export async function apiFetch<T = any>(path: string, { token, ...options }: ApiOptions): Promise<T> {
  const url = `${API_BASE_URL}${path}`;
  const requestId = generateRequestId();

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Request-Id": requestId,
        ...(options.headers ?? {}),
      },
    });

    const result = await handleApiResponse<T>(response, {
      url,
      method: options.method || 'GET',
    });

    return result;
  } catch (error) {
    // Wrap error with request info for better error messages
    const wrappedError = error instanceof ApiErrorWithStack
      ? error
      : new ApiErrorWithStack(
          error instanceof Error ? error.message : String(error),
          error instanceof Error ? error : new Error(String(error)),
          { url, method: options.method || 'GET' }
        );

    // Capture in Sentry if not already captured
    if (!(error instanceof ApiErrorWithStack)) {
      Sentry.captureException(wrappedError, {
        tags: {
          requestId,
        },
        extra: {
          url,
          method: options.method || 'GET',
        },
      });
    }

    throw wrappedError;
  }
}
